// Müşteri Listesi — plan §4.3.1 + design ref #screen-clients-list.
// Accent eyebrow + özet + FilteredTable (search/sector/size/pagination) + Boyut kolonu.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Client, ClientFacetsResponse, ClientsListResponse } from "@mahir/shared";
import { api } from "../api/client";
import { listClientFacets } from "../api/clients";
import { useAuthStore } from "../stores/auth-store";
import { FilteredTable, useDebouncedValue, type Column } from "../components/common/FilteredTable";
import { RowLink } from "../components/common/RowLink";

const PAGE_SIZE = 25;

export function ClientsList(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const canCreate = user?.permissions["clients.create"] === true;
  const canDelete = user?.permissions["clients.delete"] === true;
  const canRestore = user?.permissions["clients.restore"] === true;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [sector, setSector] = useState("");
  const [size, setSize] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);
  const [data, setData] = useState<ClientsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtre değişince sayfa 1'e dön
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sector, size, showDeleted]);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (sector) params.set("sector", sector);
    if (size) params.set("size", size);
    if (showDeleted) params.set("includeDeleted", "true");
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));

    void api<ClientsListResponse>(`/clients?${params.toString()}`)
      .then((res) => {
        if (!aborted) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Liste yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [debouncedSearch, sector, size, showDeleted, page, refreshTick]);

  // Filtre seçenekleri sunucudan (`GET /clients/facets` — tüm kayıtların distinct
  // sektör/boyutu; 2026-10-07). Uç başarısızsa görünen sayfadan türetme yedek kalır.
  const [facets, setFacets] = useState<ClientFacetsResponse | null>(null);
  useEffect(() => {
    let aborted = false;
    listClientFacets()
      .then((f) => {
        if (!aborted) setFacets(f);
      })
      .catch(() => {
        // yedek: sayfadan türetilen seçenekler
      });
    return () => {
      aborted = true;
    };
  }, [refreshTick]);
  const sectorOptions = useMemo(
    () => (facets ? toOptions(facets.sectors) : deriveOptions(data?.clients, "sector")),
    [facets, data],
  );
  const sizeOptions = useMemo(
    () => (facets ? toOptions(facets.sizes) : deriveOptions(data?.clients, "size")),
    [facets, data],
  );

  async function onDelete(c: Client) {
    if (!confirm(`"${c.name}" müşterisini silmek istediğinizden emin misiniz?`)) return;
    try {
      await api(`/clients/${c.id}`, { method: "DELETE" });
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Silinemedi");
    }
  }

  async function onRestore(c: Client) {
    try {
      await api(`/clients/${c.id}/restore`, { method: "POST" });
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Geri yüklenemedi");
    }
  }

  function refetch() {
    setRefreshTick((t) => t + 1);
  }

  const summary = data ? `${data.total} kayıtlı` : loading ? "Yükleniyor…" : "";

  const columns: Column<Client>[] = [
    {
      key: "name",
      label: "Şirket",
      render: (c) => (
        <div>
          <RowLink to={`/clients/${c.id}`}>{c.name}</RowLink>
          {c.deletedAt ? (
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--color-error)",
                fontFamily: "var(--font-mono)",
                marginTop: 2,
              }}
            >
              silindi · {new Date(c.deletedAt).toLocaleDateString("tr-TR")}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "sector",
      label: "Sektör",
      render: (c) => c.sector ?? "—",
    },
    {
      key: "country",
      label: "Ülke",
      render: (c) => c.country ?? "—",
    },
    {
      key: "size",
      label: "Boyut",
      render: (c) => c.size ?? "—",
    },
    {
      key: "projectCount",
      label: "Proje",
      align: "right",
      render: (c) => (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}>
          {c.projectCount}
        </span>
      ),
    },
    {
      key: "createdBy",
      label: "Oluşturan",
      render: (c) => (
        <span style={{ color: "var(--color-text-3)", fontSize: "0.85rem" }}>{c.createdByName}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (c) => (
        <div
          style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
          onClick={(e) => e.stopPropagation()}
        >
          {c.deletedAt ? (
            canRestore ? (
              <button
                onClick={() => void onRestore(c)}
                className="px-2.5 py-1 text-xs rounded"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-primary)",
                }}
              >
                Geri yükle
              </button>
            ) : null
          ) : canDelete ? (
            <button
              onClick={() => void onDelete(c)}
              className="px-2.5 py-1 text-xs rounded"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-error)",
              }}
            >
              Sil
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <div className="mb-6 max-w-7xl">
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-primary)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          MÜŞTERİLER
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl"
              style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
            >
              Müşteri Yönetimi
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
              {summary}
            </p>
          </div>
          <div className="flex gap-2">
            {canRestore ? (
              <label
                className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-2)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                Silinenleri göster
              </label>
            ) : null}
            {canCreate ? (
              <button
                onClick={() => void navigate("/clients/new")}
                className="px-4 py-2 rounded-lg font-semibold text-sm"
                style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
              >
                + Yeni Müşteri
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* M9-S2: error artık FilteredTable errorMessage prop'uyla render — banner kaldırıldı */}

      <div className="max-w-7xl">
        <FilteredTable
          items={data?.clients ?? []}
          columns={columns}
          rowKey={(c) => c.id}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Ad, sektör, ülke ara…"
          filters={[
            {
              key: "sector",
              label: "Sektör (tümü)",
              value: sector,
              onChange: setSector,
              options: sectorOptions,
            },
            {
              key: "size",
              label: "Boyut (tümü)",
              value: size,
              onChange: setSize,
              options: sizeOptions,
            },
          ]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
          emptyMessage={
            debouncedSearch || sector || size
              ? "Filtrelerinize uygun müşteri bulunamadı."
              : "Henüz müşteri yok"
          }
          emptyHint={
            debouncedSearch || sector || size
              ? "Filtreleri temizlemeyi deneyin."
              : "İlk müşteriyi eklemek için üstteki 'Yeni müşteri' butonunu kullanın."
          }
          errorMessage={error}
          onRetry={refetch}
          onRowClick={(c) => void navigate(`/clients/${c.id}`)}
          rowHighlight={(c) => !c.deletedAt}
        />
      </div>
    </div>
  );
}

function toOptions(values: readonly string[]): { value: string; label: string }[] {
  return values.map((v) => ({ value: v, label: v }));
}

function deriveOptions(
  items: Client[] | undefined,
  field: "sector" | "size",
): { value: string; label: string }[] {
  if (!items) return [];
  const set = new Set<string>();
  for (const it of items) {
    const v = it[field];
    if (v && v.trim().length > 0) set.add(v);
  }
  return [...set].sort().map((v) => ({ value: v, label: v }));
}
